import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TourCoordinator } from '../../services/tour-coordinator.service';
import { TourStateStore } from '../../services/tour-state-store.service';
import { TourStateStatus } from '../../config/tour-state.enum';
import { TourFeature } from '../../config/tour-feature.enum';
import { TourConfig } from '../../models/tour-config.model';
import { isDemoSessionActive } from 'src/app/modules/auth/services/demo/demo-session-flags';
import {
  DEMO_QUICK_TOUR,
  DEMO_DISCOVER_EMPLOYEE,
  DEMO_DISCOVER_TIME_RECORDING,
  DEMO_DISCOVER_ABSENCE,
} from '../../tutorials';

/**
 * Kleiner, am rechten Bildschirmrand angedockter "Daumen" — der
 * Wiedereinstieg fuer die Demo-Quick-Tour-Kette (Haupttour + die beiden
 * freiwilligen "Mehr entdecken"-Stationen), wenn der Besucher sie per X
 * geschlossen hat, statt sie zu Ende zu klicken.
 *
 * Grund fuer ein EIGENES Bauteil statt Wiederverwendung von
 * `TourResumeChipComponent`: der bestehende Chip sitzt INLINE im Planer-
 * Seiteninhalt (nur dort im Template eingebunden) und kennt nur die fuenf
 * Mentor-Touren. Die Demo-Kette spannt drei verschiedene Routen (/planner,
 * /time-recording, /absence) — ein Wiedereinstieg, der nur auf EINER Seite
 * sichtbar waere, wuerde auf den anderen beiden lautlos verschwinden. Dieses
 * Bauteil haengt deshalb global in `app.component.html` (wie Nudge/Checklist-
 * FAB) und bleibt seitenuebergreifend sichtbar, solange die Demo-Sitzung laeuft.
 *
 * Live-Feedback 10.09.2026: der Besucher soll die Tour jederzeit wegklicken
 * koennen, ohne den Faden zu verlieren — ein Klick auf den Daumen setzt exakt
 * an der Pausenposition fort (`TourCoordinator.startTour` ohne `fromStart`,
 * dieselbe Resume-Logik wie beim Mentor-Chip).
 */
/**
 * Zaehlt die Schritte, die der Besucher tatsaechlich zu sehen bekommt.
 *
 * `numberingSkip` ist die **stabile** Angabe dazu — im Gegensatz zu `skipIf`,
 * das das DOM befragt und direkt nach einem Neuladen fuer beide Bauformen einer
 * Station „ueberspringen" liefert. Der `TourCoordinator` rechnet fuer den
 * Popover nach derselben Regel (`computeVisibleNumbering`), damit Reiter und
 * Karte garantiert dieselbe Zahl zeigen.
 */
function sichtbareSchritte(config: TourConfig): number {
  const steps = config.chapters.flatMap((c) => c.steps);
  if (!config.renumberVisibleSteps) return steps.length;
  return steps.filter((s) => {
    try {
      return !s.numberingSkip?.();
    } catch {
      return true;
    }
  }).length;
}

@Component({
  selector: 'app-demo-tour-resume-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible) {
      <button
        type="button"
        class="demo-resume-tab"
        (click)="resume()"
        [attr.aria-label]="'DEMO.RESUME_TAB_ARIA' | translate: { step: stepNumber, total: totalSteps }"
      >
        <i class="fa-solid fa-play" aria-hidden="true"></i>
        <span class="demo-resume-tab__progress">{{ stepNumber }}/{{ totalSteps }}</span>
        <span
          class="demo-resume-tab__dismiss"
          role="button"
          tabindex="0"
          [attr.aria-label]="'DEMO.RESUME_TAB_DISMISS' | translate"
          (click)="dismiss($event)"
          (keydown.enter)="dismiss($event)"
          (keydown.space)="dismiss($event)"
        >
          <i class="fa-light fa-xmark" aria-hidden="true"></i>
        </span>
      </button>
    }
  `,
  styles: [
    `
      :host {
        display: contents;
      }
      /* Am rechten Rand angedockter Reiter, vertikal ueber der Mitte — so
         kollidiert er weder mit dem Help-Hub-Launcher noch dem Onboarding-
         Nudge (beide unten rechts) noch dem Demo-Banner (oben). */
      .demo-resume-tab {
        position: fixed;
        top: 45%;
        right: 0;
        transform: translateY(-50%);
        z-index: 1200;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: 1px solid var(--crystal-border-color, rgba(255, 255, 255, 0.35));
        border-right: none;
        border-radius: 10px 0 0 10px;
        padding: 8px 8px 8px 12px;
        background: var(--action-primary, #0369a1);
        color: #fff;
        font: inherit;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        box-shadow: -2px 2px 10px rgba(15, 23, 42, 0.25);
        animation: demo-resume-tab-in 0.2s cubic-bezier(0.2, 0.9, 0.3, 1.2);
      }
      .demo-resume-tab:hover {
        filter: brightness(1.08);
      }
      .demo-resume-tab__progress {
        font-variant-numeric: tabular-nums;
      }
      .demo-resume-tab__dismiss {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        margin-left: 2px;
        border-radius: 50%;
        opacity: 0.85;
      }
      .demo-resume-tab__dismiss:hover,
      .demo-resume-tab__dismiss:focus-visible {
        opacity: 1;
        background: rgba(255, 255, 255, 0.2);
      }
      @keyframes demo-resume-tab-in {
        from {
          opacity: 0;
          transform: translateY(-50%) translateX(12px);
        }
        to {
          opacity: 1;
          transform: translateY(-50%) translateX(0);
        }
      }
    `,
  ],
})
export class DemoTourResumeTabComponent implements OnInit, OnDestroy {
  /** Reihenfolge = Vorrang: die zuletzt pausierte gewinnt ohnehin (siehe recompute). */
  private static readonly CANDIDATE_TOURS: TourFeature[] = [
    TourFeature.DemoDiscoverAbsence,
    TourFeature.DemoDiscoverTimeRecording,
    TourFeature.DemoDiscoverEmployee,
    TourFeature.DemoQuickTour,
  ];
  private static readonly DISMISS_PREFIX = 'xp.demoResumeTabDismissed:';
  /**
   * Gesamtzahl Steps je Demo-Tour — fuer den Fallback, wenn `getResumeProgress`
   * `null` liefert, obwohl die Tour fortsetzbar ist (siehe {@link pickResumableTour}).
   *
   * ⚠️ **Gezaehlt wird nur, was auch angezeigt wird.** Touren mit
   * `renumberVisibleSteps` legen einander ausschliessende Bauformen als getrennte
   * Schritte ab (Demo-Quick-Tour: Toolbar- und ⋮-Menue-Fassung der
   * Mechanik-Station). Die rohe Laenge zaehlt beide und liegt damit **um eins zu
   * hoch** — der Reiter zeigte „1/5", waehrend der Popover „1/4" auswies.
   * `numberingSkip` ist genau die stabile, DOM-unabhaengige Angabe dafuer; der
   * Coordinator nutzt dieselbe Quelle.
   */
  private static readonly TOTAL_STEPS: Partial<Record<TourFeature, number>> = {
    [TourFeature.DemoQuickTour]: sichtbareSchritte(DEMO_QUICK_TOUR),
    [TourFeature.DemoDiscoverEmployee]: sichtbareSchritte(DEMO_DISCOVER_EMPLOYEE),
    [TourFeature.DemoDiscoverTimeRecording]: sichtbareSchritte(DEMO_DISCOVER_TIME_RECORDING),
    [TourFeature.DemoDiscoverAbsence]: sichtbareSchritte(DEMO_DISCOVER_ABSENCE),
  };

  visible = false;
  stepNumber = 0;
  totalSteps = 0;

  private targetTour: TourFeature | null = null;
  private lastPausedTour: TourFeature | null = null;
  private activeTour: TourFeature | null = null;
  private readonly sub = new Subscription();

  constructor(
    private readonly coordinator: TourCoordinator,
    private readonly state: TourStateStore,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!isDemoSessionActive()) return;

    this.sub.add(
      this.coordinator.activeTour$.subscribe((a) => {
        this.activeTour = a;
        this.recompute();
      }),
    );
    for (const t of DemoTourResumeTabComponent.CANDIDATE_TOURS) {
      this.sub.add(this.state.getState(t).subscribe(() => this.recompute()));
    }
    this.sub.add(
      this.coordinator.tourPaused$.subscribe(({ tourId }) => {
        if (DemoTourResumeTabComponent.CANDIDATE_TOURS.includes(tourId)) {
          this.lastPausedTour = tourId;
          this.recompute();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  resume(): void {
    if (!this.targetTour) return;
    void this.coordinator.startTour(this.targetTour, { force: true });
  }

  dismiss(event: Event): void {
    event.stopPropagation();
    if (this.targetTour) {
      const snap = this.state.getStateSync(this.targetTour);
      try {
        sessionStorage.setItem(
          this.dismissKey(this.targetTour, snap.lastStepId),
          '1',
        );
      } catch {
        /* sessionStorage kann blockiert sein — dann bleibt der Reiter sichtbar */
      }
    }
    this.visible = false;
    this.cdr.markForCheck();
  }

  private recompute(): void {
    if (this.activeTour) {
      this.targetTour = null;
      this.setHidden();
      return;
    }
    const picked = this.pickResumableTour();
    if (!picked) {
      this.targetTour = null;
      this.setHidden();
      return;
    }
    this.targetTour = picked.tour;
    this.stepNumber = picked.progress.stepNumber;
    this.totalSteps = picked.progress.totalSteps;
    this.visible = true;
    this.cdr.markForCheck();
  }

  private pickResumableTour(): {
    tour: TourFeature;
    progress: { stepNumber: number; totalSteps: number };
  } | null {
    const candidates: Array<{
      tour: TourFeature;
      progress: { stepNumber: number; totalSteps: number };
      recency: number;
    }> = [];
    for (const tour of DemoTourResumeTabComponent.CANDIDATE_TOURS) {
      const snap = this.state.getStateSync(tour);
      if (this.isDismissed(tour, snap.lastStepId)) continue;
      // Schliessen auf dem ALLERERSTEN Step leert `lastStepId` bewusst (siehe
      // tour-coordinator.service.ts "Bei i===0 leeren wir lastStepId, damit
      // die Tour sauber von vorne beginnt") — `getResumeProgress` liefert dann
      // `null`, obwohl die Tour genauso fortsetzbar (neu startbar) ist. Ohne
      // diesen Fallback verschwaende der Reiter genau in dem Fall, in dem er
      // am wichtigsten ist: der Besucher hat direkt auf Station 1 geschlossen.
      const progress =
        this.coordinator.getResumeProgress(tour) ??
        (snap.status === TourStateStatus.Started
          ? { stepNumber: 1, totalSteps: DemoTourResumeTabComponent.TOTAL_STEPS[tour] ?? 1 }
          : null);
      if (!progress) continue;
      const ts = snap.lastSeenAt ?? snap.startedAt;
      const recency = ts ? new Date(ts).getTime() : 0;
      candidates.push({
        tour,
        progress,
        recency: Number.isFinite(recency) ? recency : 0,
      });
    }
    if (!candidates.length) return null;
    candidates.sort((a, b) => {
      if (a.tour === this.lastPausedTour && b.tour !== this.lastPausedTour) return -1;
      if (b.tour === this.lastPausedTour && a.tour !== this.lastPausedTour) return 1;
      return b.recency - a.recency;
    });
    return { tour: candidates[0].tour, progress: candidates[0].progress };
  }

  private setHidden(): void {
    if (!this.visible) return;
    this.visible = false;
    this.cdr.markForCheck();
  }

  private dismissKey(tour: TourFeature, lastStepId: string | undefined): string {
    return DemoTourResumeTabComponent.DISMISS_PREFIX + tour + ':' + (lastStepId ?? '');
  }

  private isDismissed(tour: TourFeature, lastStepId: string | undefined): boolean {
    try {
      return sessionStorage.getItem(this.dismissKey(tour, lastStepId)) === '1';
    } catch {
      return false;
    }
  }
}
